import type { UserRole } from "@web/config/roles";

export type Permission =
  | `${"system" | "access"}:${string}`
  | "followups:view_history"
  | "leads:bulk_assign"
  | "leads:import_assign"
  | "leads:override_status"
  | "leads:set_initial_status"
  | "leads:view_archived"
  | `menu:${string}`
  | `report_${"daily" | "all" | "revenue" | "performance"}:view`
  | `leads:${"view" | "edit" | "transfer" | "export" | "view_reports"}_${"own" | "team" | "all"}`
  | "leads:assign_team"
  | "hierarchy:manage"
  | "hierarchy:cross_department"
  | "hierarchy:manage_lead_access"
  | "dashboard:view"
  | "calendar:view"
  | "calendar:create"
  | "calendar:edit"
  | "calendar:delete"
  | "calendar:view_restricted"
  | "calendar:set_visibility"
  | "properties:view"
  | "properties:create"
  | "properties:update"
  | "properties:delete"
  | "properties:import"
  | "properties:export"
  | "favorites:view"
  | "favorites:manage"
  | "leads:view"
  | "leads:create"
  | "leads:edit"
  | "leads:delete"
  | "leads:import"
  | "leads:export"
  | "leads:assign"
  | "leads:change_status"
  | "pipeline:view"
  | "followups:view"
  | "followups:create"
  | "followups:edit"
  | "followups:delete"
  | "kyc:view"
  | "kyc:create"
  | "kyc:edit"
  | "kyc:review"
  | "kyc:submit"
  | "kyc:download"
  | "kyc:pep_second_review"
  | "users:view"
  | "users:create"
  | "users:edit"
  | "users:delete"
  | "users:reset_password"
  | "tickets:view"
  | "tickets:create"
  | "tickets:edit"
  | "tickets:delete"
  | "tickets:assign"
  | "reports:view"
  | "reports:export"
  | "master:view"
  | "master:create"
  | "master:edit"
  | "master:delete"
  | "logs:view"
  | "notifications:view"
  | "notifications:manage"
  | "profile:view"
  | "profile:edit"
  | "roles:view"
  | "roles:edit";

export function hasPermission(
  _role: UserRole | undefined,
  permission: Permission,
  granted?: readonly string[],
) {
  const keys = granted || [];
  if (permission.startsWith("report_") || permission.startsWith("menu:"))
    return keys.includes(permission);
  if (
    permission.startsWith("hierarchy:") ||
    /^leads:.*_(own|team|all)$/.test(permission)
  )
    return keys.includes(permission);
  if (
    ["leads:", "followups:", "kyc:", "pipeline:"].some((prefix) =>
      permission.startsWith(prefix),
    )
  ) {
    const scope = (action: string) =>
      ["own", "team", "all"].some((level) =>
        keys.includes(`leads:${action}_${level}`),
      );
    if (!scope("view") && !keys.includes("leads:view")) return false;
    if (permission === "leads:view") return true;
    if (permission === "leads:edit")
      return keys.includes("leads:edit") || scope("edit");
    if (permission === "leads:assign")
      return (
        keys.includes("leads:assign") ||
        keys.includes("leads:assign_team") ||
        scope("transfer")
      );
    if (permission === "leads:export")
      return keys.includes("leads:export") || scope("export");
    return keys.includes(permission);
  }
  return keys.includes(permission);
}
